import React, { useState, useRef, useMemo, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Badge,
    Popover,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    ToggleButton,
    ToggleButtonGroup,
    Divider,
    Chip,
    Collapse,
    TextField,
    Paper,
    Grid,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSelector, useDispatch } from "react-redux";
import TopSection from "../LT/TopSection";
import CustomButton from "../../components/common/button/Button";
import { Loader } from "../../components/common/Loader";
import { MultiDropdown } from "../../components/common/MultiDropdown";
import DropdownPicker from "../../components/common/Dropdown";
import DatePickerInput from "../../components/common/DatePicker";
import dayjs from "dayjs";
import { toast } from 'react-toastify';
import Cards from "../../components/common/card/Card";
import { getPOExceptionMgmtBuyerTable } from "../../redux/actions/getPOExceptionMgmtBuyerTable";
import { getPOExceptionMgmtBuyerFilter } from "../../redux/actions/getPOExceptionMgmtBuyerFilter";
import { getPOExceptionMgmtBuyerTableEndpoint, getMaterialBatchDataEndpoint, getSupplierEmailsEndpoint, addToSupplierEmailsEndpoint, sendMailToSupplierEndpoint, sendToSupplierEndpoint, updateCollabStatusEndpoint, getPOExceptionMgmtBuyerTablefilterEndpoint } from "../../common/endpoints";
import { getMaterialBatchData } from "../../redux/actions/getMaterialBatchDataSlice";
import { getSupplierEmails } from "../../redux/actions/getSupplierEmailsSlice";
import { addToSupplierEmails } from "../../redux/actions/addToSupplierEmailsSlice";
import { sendMailToSupplier } from "../../redux/actions/sendMailToSupplierSlice";
import { sendToSupplier } from "../../redux/actions/sendToSupplierSlice";
import { updateCollabStatus } from "../../redux/actions/updateCollabStatusSlice";
import { selectDropdownOptionsData, selectDropdownOptionsLoading } from "../../redux/actions/getPOExceptionMgmtBuyerFilter";
import BatchTable from "./BatchTable";
import "./POExceptionMgmtBuyer.css"

const QtyEditCell = ({ params, onCommit, disabled }) => {
    const [localValue, setLocalValue] = useState(params.data?.QTY_EDIT ?? '');
    return (
        <TextField
            value={localValue}
            size="small"
            disabled={disabled}
            inputProps={{ style: { fontSize: '13px', padding: '4px 8px' } }}
            onChange={(e) => {
                const newVal = e.target.value;
                setLocalValue(newVal);
                onCommit(params, newVal);
            }}
            onKeyDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
        />
    );
};

const RescheduleDateCell = ({ params, onCommit, disabled }) => {
    const raw = params.data?.PO_RESCHEDULE_DATE;
    const parsed = raw && raw !== '-' ? dayjs(raw) : null;
    const [localValue, setLocalValue] = useState(parsed);
    return (
        <DatePickerInput
            value={localValue}
            size="small"
            height={32}
            fontSize="13px"
            disabled={disabled}
            onChange={(newVal) => {
                setLocalValue(newVal);
                const formatted = newVal && newVal.isValid() ? newVal.format('YYYY-MM-DD') : '';
                onCommit(params, formatted);
            }}
        />
    );
};

const POExceptionMgmtBuyer = () => {

    const getInitialFormData = () => ({
        plant: [],
        material: [],
        mrpController: [],
        supplier: [],
        poNumber: []
    });

    const formKeyToApiKey = {
        material: 'materialIds',
        plant: 'plantIds',
        mrpController: 'mrpControllers',
        supplier: 'vendorIds',
        poNumber: 'poNos',
    };

    const gridRef = useRef();
    const restoredGridFiltersRef = useRef(false);
    const filterDebounceRef = useRef(null);
    const formDataRef = useRef(getInitialFormData());
    const dispatch = useDispatch();
    const [batchModalOpen, setBatchModalOpen] = useState(false);
    const [supplierCommModalOpen, setSupplierCommModalOpen] = useState(false);
    const poExceptionMgmtBuyerTableSelector = useSelector((state) => state.getPOExceptionMgmtBuyerTable);
    const poExceptionMgmtBuyerFilterSelector = useSelector((state) => state.getPOExceptionMgmtBuyerFilter);
    const materialBatchDataSelector = useSelector((state) => state.getMaterialBatchData);
    const supplierEmailsSelector = useSelector((state) => state.getSupplierEmails);
    const selectedRegion = useSelector((state) => state.setRegionPlantStore.selectedRegion);
    const plantNameOnly = useSelector((state) => state.setRegionPlantStore.selectedPlant);
    const selectedPlant = plantNameOnly
        ? plantNameOnly.split(" - ")[0]
        : null;
    const userRole = localStorage.getItem('userRole');

    const dropdownData = useSelector(selectDropdownOptionsData);
    const dropdownLoading = useSelector(selectDropdownOptionsLoading);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalPageItems, setTotalPageItems] = useState(0);
    const [pageSize, setPageSize] = useState(500);
    const [filterCondition, setFilterCondition] = useState('withExceptions');
    const [availableFilters, setAvailableFilters] = useState({});
    const [trackingData, setTrackingData] = useState([]);
    const [selectedMaterialBatches, setSelectedMaterialBatches] = useState(null);
    const [supplierCommData, setSupplierCommData] = useState(null);
    const [disableFilterBtn, setDisableFilterBtn] = useState(true);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [savedGridFilterModel, setSavedGridFilterModel] = useState(null);
    const [formData, setFormData] = useState(getInitialFormData);
    const [gridApi, setGridApi] = useState();
    const [sendToSupplierModalOpen, setSendToSupplierModalOpen] = useState(false);
    const [selectedRecipients, setSelectedRecipients] = useState([]);
    const [filtersExpanded, setFiltersExpanded] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [addEmailFormOpen, setAddEmailFormOpen] = useState(false);
    const [newEmailData, setNewEmailData] = useState({ contactName: '', emailAddress: '' });
    const isViewerRole = ['Global Viewer', 'Regional Viewer'].includes(localStorage.getItem('userRole'));

    useEffect(() => {
        if (!dropdownData) return;

        setFormData((prev) => {
            let changed = false;
            const updated = { ...prev };

            Object.entries(formKeyToApiKey).forEach(([field, optionsKey]) => {
                const currentSelected = prev[field];
                if (!Array.isArray(currentSelected) || currentSelected.length === 0) return;

                const availableOptions = dropdownData[optionsKey];
                if (!Array.isArray(availableOptions)) return;

                const filtered = currentSelected.filter((sel) =>
                    availableOptions.some((opt) => opt.value === sel.value)
                );

                if (filtered.length !== currentSelected.length) {
                    updated[field] = filtered;
                    changed = true;
                }
            });

            return changed ? updated : prev;
        });
    }, [dropdownData]);

    useEffect(() => {
        const isAll = !selectedPlant || selectedPlant.toUpperCase() === 'ALL';
        const newPlant = isAll ? [] : [{ title: selectedPlant, value: selectedPlant }];
        const freshFormData = { ...getInitialFormData(), plant: newPlant };
        setFormData(freshFormData);
        formDataRef.current = freshFormData;
        const filterData = isAll ? getInitialFormData() : { ...getInitialFormData(), plant: [selectedPlant] };
        if (userRole && selectedRegion) {
            fetchDropdownOptions(filterData);
            fetchTable({ pageNumber: 1, pageSize, filters: freshFormData });
        }
    }, [dispatch, userRole, selectedRegion, selectedPlant]);
    

    const getOptions = (key) => {
        try {
            return (dropdownData && dropdownData[key]) ? dropdownData[key] : [];
        } catch (e) {
            return [];
        }
    };

    const handleAddEmailToggle = () => {
        setAddEmailFormOpen((prev) => !prev);
        setNewEmailData({ contactName: '', emailAddress: '' });
    };

    const handleAddEmailCancel = () => {
        setAddEmailFormOpen(false);
        setNewEmailData({ contactName: '', emailAddress: '' });
    };

    const handleAddEmailSave = async () => {
        const selectedRow = selectedRows[0] || {};
        const requestBody = {
            plantId: selectedRow.PLANT_ID || '',
            supplierId: selectedRow.VENDOR_ID || '',
            userEmail: newEmailData.emailAddress,
            parentVendorName: newEmailData.contactName,
            supplierName: selectedRow.VENDOR_NAME || '',
        };
        try {
            const response = await dispatch(addToSupplierEmails({
                dynamicEndpoint: addToSupplierEmailsEndpoint,
                data: requestBody,
            }));
            if (response?.meta?.requestStatus === 'fulfilled') {
                setAddEmailFormOpen(false);
                setNewEmailData({ contactName: '', emailAddress: '' });
                toast.success('Email address added', { autoClose: 1500 });
                const plantIds = [...new Set(selectedRows.map((r) => r.PLANT_ID).filter(Boolean))].join(',');
                const supplierIds = [...new Set(selectedRows.map((r) => r.VENDOR_ID).filter(Boolean))].join(',');
                dispatch(getSupplierEmails({
                    dynamicEndpoint: getSupplierEmailsEndpoint,
                    params: { plantIds, supplierIds },
                }));
            } else {
                toast.error('Failed to add email address. Please try again.', { autoClose: 2000 });
            }
        } catch (e) {
            console.error('Failed to add supplier email', e);
            toast.error('Failed to add email address. Please try again.', { autoClose: 2000 });
        }
    };

    const recipients = useMemo(
        () =>
            (supplierEmailsSelector?.data || []).map((item) => ({
                id: item.EMAIL_SEQ_ID,
                name: item.USER_EMAIL,
                role: (item.SUPPLIER_TYPE || '').toLowerCase(),
                email: item.USER_EMAIL,
                vendorCode: item.SUPPLIER_ID,
                company: item.SUPPLIER_NAME,
            })),
        [supplierEmailsSelector?.data]
    );

    const handleRecipientToggle = (id) => {
        setSelectedRecipients((prev) =>
            prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]
        );
    };

    const handleCloseSendModal = () => {
        setSendToSupplierModalOpen(false);
        setSelectedRecipients([]);
    };

    const handleSendNotification = async () => {
        const mrpControllerName = selectedRows[0]?.MRP_CONTROLLER_NAME || '';
        const selectedRecipientData = recipients.filter((r) => selectedRecipients.includes(r.id));
        const poNos = selectedRows.map((r) => r.PO_NO).filter(Boolean);

        const mailRequestBody = selectedRecipientData.map((r) => ({
            mrpControllerName,
            role: r.role,
            email: r.email,
            supplierId: r.vendorCode,
            supplierName: r.company,
            plantId: selectedRows[0]?.PLANT_ID || '',
            poNos,
        }));

        const currentSelectedRows = selectedRows.map(
            (row) => trackingData.find((r) => r.MGMT_ID === row.MGMT_ID) || row
        );

        const sendToSupplierRequestBody = {
            sendToSupplier: currentSelectedRows.map((row) => ({
                plantId: row.PLANT_ID || '',
                materialId: row.MATERIAL_ID || '',
                poNo: row.PO_NO || '',
                qtyEdit: row.QTY_EDIT ?? null,
                shippingInstructionsEdit: row.SHIPPING_INSTRUCTIONS_EDIT || '',
                poRescheduleDate: row.PO_RESCHEDULE_DATE || '',
                mgmt_id: row.MGMT_ID || '',
            })),
        };

        try {
            const [mailResponse, sendResponse] = await Promise.all([
                dispatch(sendMailToSupplier({
                    dynamicEndpoint: sendMailToSupplierEndpoint,
                    data: mailRequestBody,
                })),
                dispatch(sendToSupplier({
                    dynamicEndpoint: sendToSupplierEndpoint,
                    data: sendToSupplierRequestBody,
                })),
            ]);

            const bothSucceeded =
                mailResponse?.meta?.requestStatus === 'fulfilled' &&
                sendResponse?.meta?.requestStatus === 'fulfilled';

            if (bothSucceeded) {
                handleCloseSendModal();
                toast.success('Notification sent to supplier successfully', { autoClose: 2000 });
            } else {
                toast.error('Failed to send notification. Please try again.', { autoClose: 2000 });
            }
        } catch (e) {
            console.error('Failed to send notification to supplier', e);
            toast.error('Failed to send notification. Please try again.', { autoClose: 2000 });
        }
    };

    const handleViewBatches = async (materialId, materialName, plantId) => {
        setSelectedMaterialBatches({ material: materialName, materialId, plantId });
        setBatchModalOpen(true);
        try {
            await dispatch(getMaterialBatchData({
                dynamicEndpoint: getMaterialBatchDataEndpoint,
                params: { materialId, plantId },
            }));
        } catch (e) {
            console.error('Failed to fetch batch data', e);
        }
    };

    const handleBatchDialogClose = () => {
        setBatchModalOpen(false);
        setSelectedMaterialBatches(null);
    };

    const handleViewSupplierComm = (comment, poNumber) => {
        setSupplierCommData({ comm: comment, po: poNumber });
        setSupplierCommModalOpen(true);
    };

    const extractValues = (arr) => (arr || []).map((item) => (item && typeof item === 'object' ? item.value : item));

    const fetchTable = async ({ filterCondition: fc, pageNumber, pageSize: ps, filters } = {}) => {
        try {
            const fd = filters ?? formData;
            const plants = extractValues(fd.plant);
            const materials = extractValues(fd.material);
            const suppliers = extractValues(fd.supplier);
            const poNumbers = extractValues(fd.poNumber);
            const mrpControllers = extractValues(fd.mrpController);
            const requestBody = {
                filterCondition: fc ?? filterCondition,
                pageNo: pageNumber ?? currentPage,
                pageSize: ps ?? pageSize,
                plantIds: plants.some(p => String(p).toUpperCase() === 'ALL') ? [] : (plants.length === 0 && selectedPlant && selectedPlant.toUpperCase() !== 'ALL' ? [selectedPlant] : plants),
                materialIds: materials,
                mrpControllerIds: mrpControllers,
                supplierIds: suppliers,
                poNos: poNumbers,
            };
            const isGlobalRole = userRole === 'Global Planner' || userRole === 'Global Viewer';
            const response = await dispatch(getPOExceptionMgmtBuyerTable({
                dynamicEndpoint: getPOExceptionMgmtBuyerTableEndpoint,
                params: {
                    role: userRole,
                    ...(isGlobalRole ? {} : { region: selectedRegion}),
                },
                data: requestBody,
            }));
            if (response?.meta?.requestStatus === 'fulfilled') {
                const payload = response.payload;
                setTrackingData(payload?.data ?? []);
                setTotalPages(payload?.totalPages ?? 0);
                setTotalPageItems(payload?.totalNumberOfItems ?? 0);
                setCurrentPage(payload?.pageNumber ?? 1);
                setAvailableFilters(payload?.availableFilters ?? {});
            }
        } catch (e) {
            console.error('Failed to fetch PO exception table', e);
        }
    };

    useEffect(() => {
        fetchTable({ filterCondition, pageNumber: currentPage, pageSize });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterCondition, currentPage, pageSize, selectedRegion, userRole]);

    const GRID_FILTER_MODEL_STORAGE_KEY = 'poEmailManagement.gridFilterModel';
    const FORM_DATA_STORAGE_KEY = 'poEmailManagement.formData';
    const HIDDEN_COLUMNS_STORAGE_KEY = 'poEmailManagement.hiddenColumns';

    const poEmailManagementColumnDefs = [
        {
            field: 'PLANT_ID',
            headerName: 'PLANT ID',
            width: 140,
        },
        {
            field: 'PLANT_NAME',
            headerName: 'PLANT NAME',
            width: 180,
        },
        {
            field: 'MATERIAL_ID',
            headerName: 'MATERIAL ID',
            width: 160,
            cellStyle: { color: '#b91c1c', fontWeight: 'bold' },
        },
        {
            field: 'MATERIAL_NAME',
            headerName: 'MATERIAL NAME',
            width: 220,
        },
        {
            field: 'VENDOR_ID',
            headerName: 'VENDOR ID',
            width: 150,
        },
        {
            field: 'VENDOR_NAME',
            headerName: 'VENDOR NAME',
            width: 220,
        },
        {
            field: 'PURCHASING_GROUP',
            headerName: 'PURCHASING GROUP',
            width: 190,
        },
        {
            field: 'PURCHASING_GROUP_NAME',
            headerName: 'PURCHASING GROUP NAME',
            width: 240,
        },
        {
            field: 'MRP_CONTROLLER',
            headerName: 'MRP CONTROLLER',
            width: 170,
        },
        {
            field: 'MRP_CONTROLLER_NAME',
            headerName: 'MRP CONTROLLER NAME',
            width: 240,
        },
        {
            field: 'TRANSPORT_BY',
            headerName: 'TRANSPORT BY',
            width: 160,
        },
        {
            field: 'PO_TYPE',
            headerName: 'PO TYPE',
            width: 140,
        },
        {
            field: 'PO_NO',
            headerName: 'PO #',
            width: 150,
            headerClass: 'bg-gray-100',
        },
        {
            field: 'ITEM',
            headerName: 'ITEM',
            width: 110,
            headerClass: 'bg-gray-100',
        },
        {
            field: 'RELEASE_DATE',
            headerName: 'RELEASE DATE',
            width: 170,
            headerClass: 'bg-gray-100',
        },
        {
            field: 'PICK_UP_DATE',
            headerName: 'PICK UP DATE',
            width: 180,
            headerClass: 'bg-gray-100',
        },
        {
            field: 'CURRENT_DELIVERY_DATE',
            headerName: 'CURRENT DELIVERY DATE',
            width: 240,
            cellStyle: { fontWeight: 'bold' },
            headerClass: 'bg-yellow-50',
        },
        {
            field: 'MGMT_ID',
            headerName: 'PO EXCEPTION ID',
            width: 200,
            headerClass: 'bg-gray-100',
        },
        {
            field: 'EXCEPTION_MESSAGE',
            headerName: 'EXCEPTION MESSAGE',
            width: 220,
            cellStyle: { textAlign: 'center' },
            headerClass: 'bg-gray-100',
        },
        {
            field: 'PO_RESCHEDULE_DATE',
            headerName: 'PO RESCHEDULE DATE',
            width: 190,
            headerClass: 'bg-gray-100',
        },
        {
            field: 'RECORD_STATUS',
            headerName: 'SUPPLIER DECISION',
            width: 220,
            headerClass: 'bg-green-50',
        },
        {
            field: 'USER_COMMENTS',
            headerName: 'SUPPLIER COMMENT',
            width: 200,
            headerClass: 'bg-green-50',
        },
        {
            field: 'SUPPLIER_CONFIRMED_DATE',
            headerName: 'SUPPLIER CONFIRMED DATE',
            width: 240,
            headerClass: 'bg-green-50',
        },
        {
            field: 'ORIGINAL_QTY',
            headerName: 'ORIGINAL QTY',
            width: 160,
        },
        {
            field: 'QTY_EDIT',
            headerName: 'QTY (EDIT)',
            width: 160,
        },
        {
            field: 'UOM',
            headerName: 'UOM',
            width: 110,
        },
        {
            field: 'AGREEMENT',
            headerName: 'AGREEMENT',
            width: 160,
        },
        {
            field: 'GR_DATE',
            headerName: 'GR DATE',
            width: 160,
        },
        {
            field: 'AB_CONFIRMATION_DATE',
            headerName: 'AB CONFIRMATION',
            width: 200,
        },
        {
            field: 'LA_CONFIRMATION_DATE',
            headerName: 'LA CONFIRMATION',
            width: 200,
        },
        {
            field: 'ARIBA',
            headerName: 'ARIBA',
            width: 120,
            cellStyle: { textAlign: 'center' },
        },
        {
            field: 'SHIPPING_INSTRUCTIONS_ORIGINAL',
            headerName: 'SHIPPING INSTRUCTIONS ORIGINAL',
            width: 220,
        },
        {
            field: 'SHIPPING_INSTRUCTIONS_EDIT',
            headerName: 'SHIPPING INSTRUCTIONS (EDIT)',
            width: 220,
        },
        {
            field: 'INCOTERM',
            headerName: 'INCOTERM',
            width: 140,
        },
        {
            field: 'INCOTERM1',
            headerName: 'INCOTERM 1',
            width: 150,
        },
        {
            field: 'COLLABORATION_STATUS',
            headerName: 'COLLABORATION STATUS',
            width: 200,
        },
        {
            field: 'MD04_URL',
            headerName: 'MD04 URL',
            width: 130,
            sortable: false,
            filter: false,
            cellRenderer: (params) => {
                const material = params.data?.MATERIAL_ID ?? '';
                const plant = params.data?.PLANT_ID ?? '';
                const url = `https://v41ixp00.na.ko.com:4300/sap/bc/ui2/flp?sap-client=030&sap-language=EN#MRPMaterial-monitorSupplyAndDemand?Material=${material}&MRPPlant=${plant}`;
                return (
                    <IconButton
                        size="small"
                        onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
                        sx={{ color: '#000000' }}
                    >
                        <OpenInNewIcon fontSize="small" />
                    </IconButton>
                );
            },
        },
        {
            field: 'COMMAND_CENTER_URL',
            headerName: 'COMMAND CENTER URL',
            width: 130,
            sortable: false,
            filter: false,
            cellRenderer: (params) => {
                const poNumber = params.data?.PO_NO ?? '';
                const plant = params.data?.PLANT_ID ?? '';
                const url = `https://alcommandcenter.ko.com/core/supply/order/order-detail/standardPO/${poNumber}?plant=${plant}`;
                return (
                    <IconButton
                        size="small"
                        onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
                        sx={{ color: '#000000' }}
                    >
                        <OpenInNewIcon fontSize="small" />
                    </IconButton>
                );
            },
        },
    ];

    const isEmptyValue = (value) => value === null || value === undefined || value === '';
    const getValueOrDash = (value) => (isEmptyValue(value) ? '-' : value);

    const getSupplierDecisionColor = (status) => {
        switch ((status || '').toUpperCase()) {
            case 'PENDING':
                return { textColor: '#854D0E', bgColor: '#FEF9C3' };
            case 'REJECTED':
                return { textColor: '#991B1B', bgColor: '#FEE2E2' };
            case 'APPROVED':
                return { textColor: '#166534', bgColor: '#DCFCE7' };
            case 'NOT SENT':
                return { textColor: '#9A3412', bgColor: '#FFEDD5' };
            case 'COUNTER OFFER':
                return { textColor: '#492901', bgColor: '#fadead' };
            default:
                return { textColor: 'inherit', bgColor: 'transparent' };
        }
    };

    const handleDropdownChange = (params, newValue) => {
        const mgmtId = params.data?.MGMT_ID;
        setTrackingData((prevRows) =>
            prevRows.map((row) =>
                row.MGMT_ID === mgmtId
                    ? { ...row, SHIPPING_INSTRUCTIONS_EDIT: newValue }
                    : row
            )
        );
    }

    const handleCollaborationStatusChange = async (params, newValue) => {
        const mgmtId = params.data?.MGMT_ID;
        setTrackingData((prevRows) =>
            prevRows.map((row) =>
                row.MGMT_ID === mgmtId
                    ? { ...row, COLLABORATION_STATUS: newValue }
                    : row
            )
        );
        try {
            const requestBody = {
                plantId: params.data?.PLANT_ID || '',
                supplierId: params.data?.VENDOR_ID || '',
                poNo: params.data?.PO_NO || '',
                collabStatus: newValue,
            };
            const response = await dispatch(updateCollabStatus({
                dynamicEndpoint: updateCollabStatusEndpoint,
                data: requestBody,
            }));
            if (response?.meta?.requestStatus === 'fulfilled') {
                toast.success('Collaboration status updated successfully.', { autoClose: 2000 });
                fetchTable({ filterCondition, pageNumber: currentPage, pageSize, filters: formData });
            } else {
                toast.error('Failed to update collaboration status. Please try again.', { autoClose: 2000 });
                setTrackingData((prevRows) =>
                    prevRows.map((row) =>
                        row.MGMT_ID === mgmtId
                            ? { ...row, COLLABORATION_STATUS: params.data?.COLLABORATION_STATUS ?? '' }
                            : row
                    )
                );
            }
        } catch (e) {
            console.error('Failed to update collaboration status', e);
            toast.error('Failed to update collaboration status. Please try again.', { autoClose: 2000 });
        }
    };

    const handleTextInputChange = (params, newValue) => {
        const mgmtId = params.data?.MGMT_ID;
        setTrackingData((prevRows) =>
            prevRows.map((row) =>
                row.MGMT_ID === mgmtId ? { ...row, QTY_EDIT: newValue } : row
            )
        );
    };

    const handleDateInputChange = (params, newValue) => {
        const mgmtId = params.data?.MGMT_ID;
        setTrackingData((prevRows) =>
            prevRows.map((row) =>
                row.MGMT_ID === mgmtId ? { ...row, PO_RESCHEDULE_DATE: newValue } : row
            )
        );
    };



    const columnDefsWithHandlers = useMemo(() => poEmailManagementColumnDefs.map((col, index) => {
        const colKey = col.field || col.headerName;
        const isHidden = selectedColumns.includes(colKey);

        if (col.field === 'MATERIAL_ID') {
            return {
                ...col,
                hide: isHidden,
                cellRenderer: (params) => (
                    <Typography
                        sx={{
                            cursor: 'pointer',
                            color: '#DC2626',
                            fontWeight: 500,
                            fontSize: '14px',
                            '&:hover': {
                                textDecoration: 'underline',
                            },
                        }}
                        onClick={() => handleViewBatches(params.value, params.data?.MATERIAL_NAME, params.data?.PLANT_ID)}
                    >
                        {params.value}
                    </Typography>
                )
            };
        }
        if (col.field === 'PO_NO') {
            return {
                ...col,
                hide: isHidden,
                cellRenderer: (params) => (
                    <Typography sx={{
                        color: '#DC2626', fontWeight: 500, fontSize: '14px', '&:hover': {
                            textDecoration: 'underline',
                        },
                    }}>
                        {params.value}
                    </Typography>
                )

            };
        }
        if (col.field === 'USER_COMMENTS') {
            return {
                ...col,
                hide: isHidden,
                valueGetter: (params) => params.data?.USER_COMMENTS ?? '',
                cellRenderer: (params) => (
                    !isEmptyValue(params.value) ? (
                        <Typography
                            sx={{
                                color: '#DC2626',
                                fontWeight: 400,
                                fontSize: '14px',
                                cursor: 'pointer',
                                '&:hover': {
                                    textDecoration: 'underline',
                                },
                            }}
                            onClick={() => handleViewSupplierComm(params.value, params.data?.PO_NO)}
                        >
                            View Log
                        </Typography>
                    ) : '-'
                )
            };
        }
        if (col.field === 'RECORD_STATUS') {
            return {
                ...col,
                hide: isHidden,
                cellRenderer: (params) => {
                    const status = params.value;
                    if (isEmptyValue(status)) return '-';
                    const { textColor, bgColor } = getSupplierDecisionColor(status);
                    return (
                        <Typography
                            sx={{
                                color: textColor,
                                backgroundColor: bgColor,
                                fontWeight: 500,
                                px: 1,
                                py: 0.25,
                                borderRadius: '10px',
                                display: 'inline-block',
                                fontSize: '12px',
                            }}
                        >
                            {status}
                        </Typography>
                    );
                },
            };
        }
        if (col.field === 'SHIPPING_INSTRUCTIONS_EDIT') {
            return {
                ...col,
                hide: isHidden,
                cellRenderer: (params) => (
                    <div
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <DropdownPicker
                            value={isEmptyValue(params.value) || params.value === '-' ? '' : params.value}
                            multiple={false}
                            disabled={isViewerRole}
                            options={[
                                { value: 'Truck', label: 'Truck' },
                                { value: 'Ocean', label: 'Ocean' },
                                { value: 'Air', label: 'Air' },
                                { value: 'Rail', label: 'Rail' },
                            ]}
                            handleChange={(e) => handleDropdownChange(params, e.target.value)}
                        />
                    </div>
                )
            };
        }
        if (col.field === 'COLLABORATION_STATUS') {
            return {
                ...col,
                hide: isHidden,
                cellRenderer: (params) => {
                    const recordStatus = (params.data?.RECORD_STATUS || '').toUpperCase();
                    const showButtons = ['APPROVED', 'REJECTED', 'COUNTER OFFER'].includes(recordStatus);
                    const current = isEmptyValue(params.value) || params.value === '-' ? '' : params.value;
                    if (!showButtons) {
                        return <span>{current || '-'}</span>;
                    }
                    if (current) {
                        return (
                            <Typography
                                sx={{
                                    fontWeight: 500,
                                    fontSize: '12px',
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: '10px',
                                    display: 'inline-block',
                                    color: current === 'Accept' ? '#15803d' : '#b45309',
                                    backgroundColor: current === 'Accept' ? '#DCFCE7' : '#FEF3C7',
                                }}
                            >
                                {current}
                            </Typography>
                        );
                    }
                    if (isViewerRole) {
                        return <span>-</span>;
                    }
                    return (
                        <div
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            style={{ display: 'flex', gap: '6px', alignItems: 'center', height: '100%' }}
                        >
                            <button
                                onClick={() => handleCollaborationStatusChange(params, current === 'Accept' ? '' : 'Accept')}
                                style={{
                                    padding: '3px 10px',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    borderRadius: '6px',
                                    border: `1px solid ${current === 'Accept' ? '#15803d' : '#D1D5DB'}`,
                                    cursor: 'pointer',
                                    backgroundColor: current === 'Accept' ? '#DCFCE7' : '#fff',
                                    color: current === 'Accept' ? '#15803d' : '#374151',
                                }}
                            >
                                Accept
                            </button>
                            <button
                                onClick={() => handleCollaborationStatusChange(params, current === 'Defer' ? '' : 'Defer')}
                                style={{
                                    padding: '3px 10px',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    borderRadius: '6px',
                                    border: `1px solid ${current === 'Defer' ? '#b45309' : '#D1D5DB'}`,
                                    cursor: 'pointer',
                                    backgroundColor: current === 'Defer' ? '#FEF3C7' : '#fff',
                                    color: current === 'Defer' ? '#b45309' : '#374151',
                                }}
                            >
                                Defer
                            </button>
                        </div>
                    );
                }
            };
        }
        if (col.field === 'PO_RESCHEDULE_DATE') {
            return {
                ...col,
                hide: isHidden,
                width: 220,
                cellRenderer: (params) => (
                    <RescheduleDateCell params={params} onCommit={handleDateInputChange} disabled={isViewerRole} />
                ),
            };
        }
        if (col.field === 'QTY_EDIT') {
            return {
                ...col,
                hide: isHidden,
                cellRenderer: (params) => (
                    <QtyEditCell params={params} onCommit={handleTextInputChange} disabled={isViewerRole} />
                )
            };
        }
        return {
            ...col,
            hide: isHidden,
            valueFormatter: (params) => getValueOrDash(params.value),
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [selectedColumns]);

    const filterButtonStyles = {
        textTransform: "none",
        borderRadius: "8px",
        px: 2,
        border: "1px solid #E5E7EB",
        "&.Mui-selected": {
            backgroundColor: "#111827",
            color: "#fff",
            "&:hover": {
                backgroundColor: "#111827",
            },
        },
    };

    const handleSendToSupplier = () => {
        if (selectedRows.length === 0) {
            toast.error('Please select at least one row to send to supplier.', { autoClose: 2000 });
            return;
        }
        const plantIds = [...new Set(selectedRows.map((r) => r.PLANT_ID).filter(Boolean))].join(',');
        const supplierIds = [...new Set(selectedRows.map((r) => r.VENDOR_ID).filter(Boolean))].join(',');
        dispatch(getSupplierEmails({
            dynamicEndpoint: getSupplierEmailsEndpoint,
            params: { plantIds, supplierIds },
        }));
        setSendToSupplierModalOpen(true);
    };

    const handleFilterConditionChange = (newCondition) => {
        setFilterCondition(newCondition);
        setCurrentPage(1);
        fetchTable({ filterCondition: newCondition, pageNumber: 1, pageSize, filters: formData });
    };

    const handleClickHide = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseHide = () => {
        setAnchorEl(null);
    };

    const handleColumnChange = (event) => {
        setSelectedColumns(event.target.value || []);
    };

    const renderDropdownValue = (selected) => {
        if (!selected || selected.length === 0) return <em>Select Columns</em>;
        return selected
            .map((field) => columnFilterDropdownOption.find((opt) => opt.value === field)?.label || field)
            .join(', ');
    };

    const handleSave = () => {
        setAnchorEl(null);
        toast.success('Columns updated', { autoClose: 1000 });
    };

    const clearAllFilters = () => {
        if (gridRef.current) {
            gridRef.current.setFilterModel(null);
            setDisableFilterBtn(true);
            try {
                sessionStorage.removeItem(GRID_FILTER_MODEL_STORAGE_KEY);
            } catch (e) {
                // ignore storage errors
            }
        }
    };

    useEffect(() => {
        try {
            const savedFormDataRaw = sessionStorage.getItem(FORM_DATA_STORAGE_KEY);
            if (savedFormDataRaw) {
                const parsed = JSON.parse(savedFormDataRaw);
                if (parsed && typeof parsed === 'object') {
                    setFormData((prev) => ({ ...prev, ...parsed }));
                }
            }

            const savedFilterModelRaw = sessionStorage.getItem(GRID_FILTER_MODEL_STORAGE_KEY);
            if (savedFilterModelRaw) {
                const parsed = JSON.parse(savedFilterModelRaw);
                if (parsed && typeof parsed === 'object') {
                    setSavedGridFilterModel(parsed);
                }
            }

            const savedColumnsRaw = sessionStorage.getItem(HIDDEN_COLUMNS_STORAGE_KEY);
            if (savedColumnsRaw) {
                const parsed = JSON.parse(savedColumnsRaw);
                if (Array.isArray(parsed)) {
                    setSelectedColumns(parsed);
                }
            }
        } catch (e) {
            // ignore restore errors
        }
    }, []);

    useEffect(() => {
        try {
            sessionStorage.setItem(HIDDEN_COLUMNS_STORAGE_KEY, JSON.stringify(selectedColumns || []));
        } catch (e) {
            // ignore storage errors
        }
    }, [selectedColumns]);

    useEffect(() => {
        if (!gridApi || restoredGridFiltersRef.current) return;

        if (savedGridFilterModel && Object.keys(savedGridFilterModel).length > 0) {
            gridApi.setFilterModel(savedGridFilterModel);
            setDisableFilterBtn(false);
        }

        restoredGridFiltersRef.current = true;
    }, [gridApi, savedGridFilterModel]);


    const onGridReady = (params) => {
        setGridApi(params?.api);
        gridRef.current = params?.api;
    }

    const onSelectionChanged = (params) => {
        const rows = params.api.getSelectedRows();
        const uniqueVendorIds = [...new Set(rows.map((r) => r.VENDOR_ID).filter(Boolean))];
        if (uniqueVendorIds.length > 1) {
            toast.error('Please select rows belonging to the same Vendor ID only.', { autoClose: 3000 });
            params.api.deselectAll();
            setSelectedRows([]);
            return;
        }
        setSelectedRows(rows);
    };

    const onFilterChanged = () => {
        if (gridRef.current) {
            const filterModel = gridRef.current.getFilterModel();
            try {
                sessionStorage.setItem(GRID_FILTER_MODEL_STORAGE_KEY, JSON.stringify(filterModel || {}));
            } catch (e) {
                // ignore storage errors
            }
            setDisableFilterBtn(Object.keys(filterModel).length === 0);
        }
    };

    const gridOptions = {
        getRowId: (params) => {
            const d = params.data || {};
            return d.MGMT_ID;
        },
        onFilterChanged,
        overlayLoadingTemplate: `<span class="ag--overlay-loading-center" >Please wait while data is loading</span>`,
        overlayNoRowsTemplate: `<span class="ag--overlay-no-rows-center" >No Data Available</span>`,
        defaultColDef: {
            sortable: true,
            filter: 'agMultiColumnFilter',
            filterParams: {
                filters: [
                    {
                        filter: 'agTextColumnFilter',
                        filterParams: {
                            suppressMiniFilter: false,
                        },
                    },
                    {
                        filter: 'agSetColumnFilter',
                        filterParams: {
                            excelMode: 'windows',
                            suppressMiniFilter: false,
                            buttons: ['apply', 'reset'],
                        },
                    },
                ],
            },
            cellStyle: {
                whiteSpace: "normal",
                wordBreak: "break-word",
                lineHeight: 1.5,
                display: "flex",
                alignItems: "center",
            },
            valueGetter: (params) => {
                if (!params.data) return "-";

                const field = params.colDef.field;
                const value = params.data[field];

                return value === null || value === undefined || value === "" ? "-" : value;
            },
        }

    }

    const columnFilterDropdownOption = useMemo(() => (
        poEmailManagementColumnDefs
            .filter((c) => c.field || c.headerName)
            .map((c) => ({
                value: c.field || c.headerName,
                label: c.headerName || c.field,
            }))
    ), []);

    const onPageSizeChanged = (event) => {
        const newSize = Number(event?.target?.value);
        setPageSize(newSize);
        setCurrentPage(1);
        fetchTable({ pageNumber: 1, pageSize: newSize, filters: formData });
    };

    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            localStorage.setItem("lastVisitedPage", page);
            fetchTable({ pageNumber: page, pageSize, filters: formData });
        }
    };

    const getRange = () => {
        const start = (currentPage - 1) * pageSize + 1;
        const end = Math.min(currentPage * pageSize, totalPageItems);
        return `${start} - ${end}`;
    };

    const rowSelection = {
        mode: "multiRow",
        headerCheckbox: true,
    };

    const fetchDropdownOptions = async (filterdata) => {
        try {
            const transformedFilterData = Object.fromEntries(
                Object.entries(filterdata || {}).map(([key, val]) => {
                    const apiKey = formKeyToApiKey[key] || key;
                    if (Array.isArray(val)) {
                        return [apiKey, val.map((v) => (v && typeof v === 'object' && 'value' in v) ? v.value : v)];
                    }
                    return [apiKey, val];
                })
            );
            const isGlobalRole = userRole === 'Global Planner' || userRole === 'Global Viewer';
            const response = await dispatch(getPOExceptionMgmtBuyerFilter({
                dynamicEndpoint: getPOExceptionMgmtBuyerTablefilterEndpoint,
                params: {
                    role: userRole,
                    ...(isGlobalRole ? {} : { region: selectedRegion }),
                },
                data: transformedFilterData,
            }));
            if (response?.meta?.requestStatus === 'fulfilled') {
                const options = response.payload || [];
                return options.map((opt) => ({ title: String(opt), value: String(opt) }));
            }
            return [];
        } catch (e) {
            console.error(`Failed to fetch dropdown options for ${JSON.stringify(filterdata)}`, e);
            return [];
        }
    };

    const handleSelectedItemsChange = (name, newSelectedItems) => {
        setFormData((prev) => {
            const next = { ...prev, [name]: newSelectedItems };
            formDataRef.current = next;
            return next;
        });
    };

    const handleDropdownClose = (name, finalSelectedItems) => {
        const updatedFormData = {
            ...formDataRef.current,
            [name]: finalSelectedItems,
        };
        formDataRef.current = updatedFormData;
        setFormData(updatedFormData);
        const normalizedFormData = Object.fromEntries(
            Object.entries({ ...updatedFormData, role: 'Admin', region: selectedRegion }).map(([key, val]) => {
                if (Array.isArray(val)) {
                    return [key, val.map(v => (v && typeof v === 'object' && 'value' in v) ? v.value : v)];
                }
                return [key, val];
            })
        );
        fetchDropdownOptions(normalizedFormData);
    };

    const filterOptions = useMemo(() => {
        const toOptions = (arr) => (arr || []).map((v) => ({ title: String(v), value: String(v) }));
        return {
            plant: toOptions(availableFilters.plantIds),
            material: toOptions(availableFilters.materialIds),
            mrpController: toOptions(availableFilters.mrpControllerIds),
            supplier: toOptions(availableFilters.supplierIds),
            poNumber: toOptions(availableFilters.poNos),
        };
    }, [availableFilters]);

    const generateFiltersTitle = () => {
        const activeFilters = Object.keys(formData).filter(
            (key) => formData[key] && formData[key].length > 0
        );

        return `Filter Selection Panel ${activeFilters.length > 0
            ? ` (Active Filters: ${activeFilters.join(', ')})`
            : ' (Active Filters: None)'
            }`;
    };

    const handleClearFilters = () => {
        const cleared = getInitialFormData();
        setFormData(cleared);
        setFiltersExpanded(false);
        setCurrentPage(1);
        fetchTable({ pageNumber: 1, pageSize, filters: cleared });
        fetchDropdownOptions(cleared);
    }

    const applyAndCollapse = () => {
        setFiltersExpanded(false);
        setCurrentPage(1);
        fetchTable({ pageNumber: 1, pageSize, filters: formData });
    }

    const hasSelectedFilters = useMemo(() => {
        return Object.values(formData).some(
            (value) => Array.isArray(value) && value.length > 0
        );
    }, [formData]);

    return (
        <div>
            <TopSection
                labelList={[
                    { title: "Home", path: "/dashboard" },
                    { title: "PO Exception Management" },
                ]}
                headerName="PO Exception Management"
                hideHeader
            />

            <Box sx={{ flexGrow: 1, p: 1 }}>
                {/* Filter Panel */}
                <Paper sx={{ p: 1, mb: 1 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            cursor: 'pointer',
                            userSelect: 'none',
                            margin: '0px'
                        }}
                        onClick={() => setFiltersExpanded(!filtersExpanded)}
                    >
                        <h3 className="po-filters-title">{generateFiltersTitle()}</h3>
                        <IconButton
                            size="small"
                            sx={{
                                transform: filtersExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                                transition: 'transform 0.3s ease',
                                padding: '4px',
                            }}
                        >
                            <ExpandMoreIcon />
                        </IconButton>
                    </Box>
                    <Collapse in={filtersExpanded} timeout="auto" unmountOnExit>
                        <Grid container spacing={1} sx={{ mt: 0 }}>
                            <Grid item xs={2}>
                                <div style={{ margin: 5 }}>
                                    <label className="me-label">Plant</label>
                                    <MultiDropdown
                                        id="plant"
                                        name="plant"
                                        options={getOptions('plantIds')}
                                        selectedItemsData={formData.plant || []}
                                        onChangeItems={handleSelectedItemsChange}
                                        onDropdownClose={handleDropdownClose}
                                        height={35}
                                        disabled={dropdownLoading}
                                    />
                                </div>
                            </Grid>
                            <Grid item xs={2}>
                                <div style={{ margin: 5 }}>
                                    <label className="me-label">Material</label>
                                    <MultiDropdown
                                        id="material"
                                        name="material"
                                        options={getOptions('materialIds')}
                                        selectedItemsData={formData.material || []}
                                        onChangeItems={handleSelectedItemsChange}
                                        onDropdownClose={handleDropdownClose}
                                        height={35}
                                        disabled={dropdownLoading}
                                    />
                                </div>
                            </Grid>
                            <Grid item xs={2}>
                                <div style={{ margin: 5 }}>
                                    <label className="me-label">MRP Controller</label>
                                    <MultiDropdown
                                        id="mrpController"
                                        name="mrpController"
                                        options={getOptions('mrpControllers')}
                                        selectedItemsData={formData.mrpController || []}
                                        onChangeItems={handleSelectedItemsChange}
                                        onDropdownClose={handleDropdownClose}
                                        height={35}
                                        disabled={dropdownLoading}
                                    />
                                </div>
                            </Grid>
                            <Grid item xs={2}>
                                <div style={{ margin: 5 }}>
                                    <label className="me-label">Supplier</label>
                                    <MultiDropdown
                                        id="supplier"
                                        name="supplier"
                                        options={getOptions('vendorIds')}
                                        selectedItemsData={formData.supplier || []}
                                        onChangeItems={handleSelectedItemsChange}
                                        onDropdownClose={handleDropdownClose}
                                        height={35}
                                        disabled={dropdownLoading}
                                    />
                                </div>
                            </Grid>
                            <Grid item xs={2}>
                                <div style={{ margin: 5 }}>
                                    <label className="me-label">PO #</label>
                                    <MultiDropdown
                                        id="poNumber"
                                        name="poNumber"
                                        options={getOptions('poNos')}
                                        selectedItemsData={formData.poNumber || []}
                                        onChangeItems={handleSelectedItemsChange}
                                        onDropdownClose={handleDropdownClose}
                                        height={35}
                                        disabled={dropdownLoading}
                                    />
                                </div>
                            </Grid>

                        </Grid>
                        <Box display="flex" justifyContent="flex-end" gap={2} sx={{ mx: 1, mb: 2 }}>
                            <CustomButton
                                label="Clear Filters"
                                type="primary"
                                height="35px"
                                onClick={handleClearFilters}
                            />
                            <CustomButton
                                label="Apply and Collapse"
                                type="secondary"
                                height="35px"
                                disabled={!hasSelectedFilters}
                                onClick={applyAndCollapse}
                            />
                        </Box>
                    </Collapse>
                </Paper>
                {/* Summary Cards */}
                {/* <div className="container">
                <Cards cards={cardData} showBorder={true} callBackFunction={callBackFunction} />
            </div> */}
                <Box sx={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    margin: '0px',
                    backgroundColor: '#f5f5f5',
                    height: 'auto',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                    padding: '12px',
                    borderRadius: '8px',
                }}>
                    {/* Quick Filters */}
                    <Box display="flex" alignItems="center" gap={1} sx={{ mt: 1, mx: 1 }}>
                        <Typography fontWeight={600}>Quick Filters:</Typography>
                        <ToggleButtonGroup
                            value={filterCondition}
                            exclusive
                            onChange={(e, val) => { if (val !== null) handleFilterConditionChange(val); }}
                        >
                            <ToggleButton value="all" sx={filterButtonStyles}>All</ToggleButton>
                            <ToggleButton value="actionNeeded" sx={filterButtonStyles}>Action Needed</ToggleButton>
                            <ToggleButton value="withExceptions" sx={filterButtonStyles}>Exceptions Management</ToggleButton>
                            <ToggleButton value="supplierResponses" sx={filterButtonStyles}>Supplier Responses</ToggleButton>
                            <ToggleButton value="notified" sx={filterButtonStyles}>Notified</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                    <Box display="flex" flexDirection="row" justifyContent="space-between" gap={2} sx={{ mt: 2, mb: 1 }}>
                        {/* Filter Buttons */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <IconButton onClick={handleClickHide}>
                                    <Badge badgeContent={selectedColumns.length} sx={{
                                        "& .MuiBadge-badge": {
                                            fontSize: "0.6rem", // Smaller font size
                                            height: "16px", // Adjust badge height
                                            minWidth: "16px", // Adjust badge width
                                            padding: "4px", // Adjust padding
                                            background: '#000',
                                            color: '#fff'
                                        },
                                    }}>
                                        <MoreVertIcon />
                                    </Badge>
                                </IconButton>

                                <Popover
                                    open={Boolean(anchorEl)}
                                    anchorEl={anchorEl}
                                    onClose={handleCloseHide}
                                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                                    className="hidePopover"
                                    sx={{
                                        marginTop: 1,
                                        marginLeft: 0,
                                    }}
                                >
                                    <Box sx={{ padding: '10px', width: 150 }}>
                                        <p style={{ marginBottom: 10, marginTop: 5 }}>Hide Columns</p>
                                        <Select
                                            multiple
                                            value={selectedColumns}
                                            onChange={handleColumnChange}
                                            renderValue={renderDropdownValue}
                                            displayEmpty
                                            sx={{ backgroundColor: "#fff", border: '1px solid #000', width: '100%', height: 35, marginBottom: 5 }}
                                        >
                                            <MenuItem disabled value="">
                                                <em>Select Columns</em>
                                            </MenuItem>
                                            {columnFilterDropdownOption?.map(col => (
                                                <MenuItem key={col.value} value={col.value}>
                                                    <Checkbox checked={selectedColumns?.includes(col.value)} />
                                                    <ListItemText primary={col.label} />
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <CustomButton type="secondary" label="Save" onClick={handleSave} />
                                    </Box>
                                </Popover>
                            </Box>

                            <CustomButton
                                maxWidth="150px"
                                label="Clear All Filters"
                                bgColor="black"
                                color="white"
                                disabled={disableFilterBtn}
                                onClick={clearAllFilters}
                            />
                        </Box>
                        {/* Action Buttons */}
                        <Box display="flex" alignSelf="flex-end" gap={2}>
                            <CustomButton
                                label="Send to Supplier"
                                type="secondary"
                                maxWidth="200px"
                                disabled={!(filterCondition === 'withExceptions' || filterCondition === 'actionNeeded') || isViewerRole}
                                onClick={handleSendToSupplier}
                            />
                        </Box>
                    </Box>
                    <div style={{ margin: "10px 0px" }}>
                        <div className="ag-theme-quartz" style={{ height: 400, width: "100%" }}>
                            {poExceptionMgmtBuyerTableSelector.loading ?
                                <Loader />
                                :
                                <AgGridReact
                                    ref={gridRef}
                                    rowData={trackingData || []}
                                    columnDefs={columnDefsWithHandlers}
                                    onGridReady={onGridReady}
                                    onSelectionChanged={onSelectionChanged}
                                    gridOptions={gridOptions}
                                    rowSelection={rowSelection}
                                />}
                        </div>
                        <div className="customPagination">
                            <div className="pagSize_pagination">
                                <p style={{ margin: '5px' }}>Page Size:</p>
                                <select onChange={onPageSizeChanged} value={pageSize} style={{ width: '70px', borderRadius: '5px', borderColor: 'lightgray', padding: '5px' }}>
                                    <option value={10}>10</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                    <option value={500}>500</option>
                                    <option value={1000}>1000</option>
                                </select>
                            </div>
                            <div style={{ margin: '0 25px' }}>
                                <span>{`${getRange()} of ${totalPageItems}`}</span>
                            </div>
                            <div>
                                <IconButton onClick={() => goToPage(1)} disabled={currentPage === 1} style={{ padding: 0 }}>
                                    <SkipPreviousIcon />
                                </IconButton>
                                <IconButton onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>
                                    <ArrowLeftIcon />
                                </IconButton>
                                <span>{`Page ${currentPage} of ${totalPages}`}</span>
                                <IconButton onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>
                                    <ArrowRightIcon />
                                </IconButton>
                                <IconButton onClick={() => goToPage(totalPages)} disabled={currentPage === totalPages} style={{ padding: 0 }}>
                                    <SkipNextIcon />
                                </IconButton>
                            </div>
                        </div>
                    </div>

                </Box>
                <BatchTable
                    isShown={batchModalOpen}
                    selectedMaterial={selectedMaterialBatches?.material}
                    materialBatchesData={materialBatchDataSelector?.data ?? []}
                    loading={materialBatchDataSelector?.loading}
                    handleBatchDialogClose={handleBatchDialogClose}
                />
                <Dialog open={sendToSupplierModalOpen} onClose={handleCloseSendModal} fullWidth maxWidth="sm">
                    <DialogTitle sx={{ pb: 0.5 }}>
                        <Typography variant="h6" fontWeight={600}>Send Notification to Supplier</Typography>
                        <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>
                            {selectedRows.length} PO(s) selected • Select recipients to notify about changes
                        </Typography>
                    </DialogTitle>
                    <DialogContent dividers>
                        {/* Selected Purchase Orders */}
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Selected Purchase Orders</Typography>
                        <Box sx={{ p: 1.5, backgroundColor: '#F9FAFB', borderRadius: '6px', border: '1px solid #E5E7EB', mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {selectedRows.map((row, idx) => (
                                <Box key={idx} sx={{ borderBottom: idx < selectedRows.length - 1 ? '1px solid #E5E7EB' : 'none', pb: idx < selectedRows.length - 1 ? 1 : 0 }}>
                                    <Typography variant="body2" fontWeight={500}>{row.PO_NO}/{row.ITEM}</Typography>
                                    <Typography variant="body2" sx={{ color: '#6B7280' }}>{row.MATERIAL_NAME}</Typography>
                                </Box>
                            ))}
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        {/* Recipients */}
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>Select Recipients</Typography>
                        {supplierEmailsSelector?.loading ? (
                            <Typography variant="body2" sx={{ color: '#6B7280', py: 1 }}>Loading recipients...</Typography>
                        ) : recipients.length === 0 ? (
                            <Typography variant="body2" sx={{ color: '#6B7280', py: 1 }}>No recipients found.</Typography>
                        ) : null}
                        {!supplierEmailsSelector?.loading && recipients.map((recipient) => (
                            <Box
                                key={recipient.id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 1.5,
                                    py: 1.5,
                                    px: 1,
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    '&:hover': { backgroundColor: '#F9FAFB' },
                                }}
                                onClick={() => handleRecipientToggle(recipient.id)}
                            >
                                <Checkbox
                                    checked={selectedRecipients.includes(recipient.id)}
                                    onChange={() => handleRecipientToggle(recipient.id)}
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{ p: 0, mt: 0.25 }}
                                />
                                <Box sx={{ flexGrow: 1 }}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Typography variant="body2" fontWeight={500}>{recipient.name}</Typography>
                                        <Chip
                                            label={recipient.role}
                                            size="small"
                                            sx={{
                                                height: 18,
                                                fontSize: '10px',
                                                backgroundColor: recipient.role === 'parent' ? '#DBEAFE' : '#F3E8FF',
                                                color: recipient.role === 'parent' ? '#1D40AE' : '#6B21A8',
                                            }}
                                        />
                                    </Box>
                                    <Typography variant="body2" sx={{ color: '#6B7280' }}>{recipient.email}</Typography>
                                    <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                                        {recipient.vendorCode} • {recipient.company}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                        <Box sx={{ mt: 1.5, pl: 1 }}>
                            <Typography
                                variant="body2"
                                onClick={handleAddEmailToggle}
                                sx={{ color: '#2563EB', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                            >
                                + Add new email address
                            </Typography>
                            <Collapse in={addEmailFormOpen} timeout="auto" unmountOnExit>
                                <Box
                                    sx={{
                                        mt: 1.5,
                                        p: 2,
                                        backgroundColor: '#F9FAFB',
                                        border: '1px solid #E5E7EB',
                                        borderRadius: '8px',
                                    }}
                                >
                                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                                        Add Email Address
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="Contact Name"
                                            value={newEmailData.contactName}
                                            onChange={(e) => setNewEmailData((prev) => ({ ...prev, contactName: e.target.value }))}
                                        />
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="Email Address"
                                            value={newEmailData.emailAddress}
                                            onChange={(e) => setNewEmailData((prev) => ({ ...prev, emailAddress: e.target.value }))}
                                        />

                                    </Box>
                                    <Box display="flex" gap={1} sx={{ mt: 2 }}>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={handleAddEmailSave}
                                            disabled={!newEmailData.contactName || !newEmailData.emailAddress}
                                            sx={{
                                                backgroundColor: '#000000',
                                                '&:hover': { backgroundColor: '#000000' },
                                                '&.Mui-disabled': { backgroundColor: '#dbdbdc', color: '#fff' },
                                                textTransform: 'none',
                                                color: '#fff',
                                                fontWeight: 500,
                                            }}
                                        >
                                            Save
                                        </Button>
                                        <Button
                                            variant="text"
                                            size="small"
                                            onClick={handleAddEmailCancel}
                                            sx={{
                                                textTransform: 'none',
                                                color: '#374151',
                                                '&:hover': { backgroundColor: '#F3F4F6' },
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                    </Box>
                                </Box>
                            </Collapse>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                        <Button
                            variant="outlined"
                            onClick={handleCloseSendModal}
                            sx={{
                                textTransform: 'none',
                                borderColor: '#D1D5DB',
                                color: '#374151',
                                '&:hover': { borderColor: '#9CA3AF', backgroundColor: '#F9FAFB' },
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            disabled={selectedRecipients.length === 0}
                            onClick={handleSendNotification}
                            sx={{
                                backgroundColor: '#000',
                                '&:hover': { backgroundColor: '#333' },
                                '&.Mui-disabled': { backgroundColor: '#E5E7EB', color: '#9CA3AF' },
                                textTransform: 'none',
                                color: '#fff',
                            }}
                        >
                            Send Notification ({selectedRecipients.length})
                        </Button>
                    </DialogActions>
                </Dialog>
                <Dialog open={supplierCommModalOpen} onClose={() => setSupplierCommModalOpen(false)} fullWidth maxWidth="sm">
                    <Box sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>Communication Log - {supplierCommData?.po}</Typography>
                        <Box sx={{
                            maxHeight: 400,
                            overflowY: 'auto',
                            padding: 2,
                            backgroundColor: '#f9f9f9',
                            borderRadius: '8px',
                            border: '1px solid #ddd',
                        }}>
                            <Typography sx={{ whiteSpace: 'pre-line' }}>{supplierCommData?.comm}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="flex-end">
                            <Button
                                variant="contained"
                                onClick={() => setSupplierCommModalOpen(false)}
                                sx={{
                                    backgroundColor: "#000",
                                    "&:hover": { backgroundColor: "#000" },
                                    textTransform: "none",
                                    color: "#fff",
                                    mt: 2,
                                }}
                            >
                                Close
                            </Button>
                        </Box>
                    </Box>
                </Dialog>
            </Box>
        </div>
    );
};

export default POExceptionMgmtBuyer;